import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  ClickAwayListener,
  Box,
  Divider,
  List,
  ListItemText,
  Typography,
  Switch,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { isEmpty, orderBy } from 'lodash'
import { useTranslation } from 'react-i18next'
import { generatePath, useHistory } from 'react-router-dom'

import BusinessIcon from '../../assets/images/icons/business.svg?react'
import {
  AppActions,
  CONTRACT_STATUS_ORDER,
  displayStatusKeys,
  statusActions,
} from '../../constants'
import {
  ActionSignatureStatus,
  BankableEntityContractType,
  ContractStatusType,
  ContractType,
  Individual,
  LegalEntity,
  UboLegalEntityType,
  UboType,
  useActionSignatureRequestsCountLazyQuery,
  useGetUserContractsQuery,
} from '../../graphql'
import { goBackPathVar, isUnsaveFormData } from '../../graphql/local'
import {
  CurrentUserContext,
  useCustomHistory,
  useDetermineUserRights,
  useLimitedPerson,
  usePageFiltersSorting,
  useTotalUnreadDocCount,
} from '../../hooks'
import { CmTabName } from '../../pages/ContractManagementPage'
import { APP_PATHS, PATH_PARAMS } from '../../routes/paths'
import {
  getDetailsContractsByIndividual,
  isActiveContract,
  isLimitedActiveContract,
} from '../../utils'

import { ContractAlertDot } from '../Common/Selectors/ContractAlertDot'
import { ContractsListItem } from './ContractsListItem'
import { SubAccordionMenu } from './SubAccordionMenu'
import {
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionSummary,
  StyledFormLabel,
  StyledListItem,
  StyledTypography,
} from './StyledComponents'

export const CompanySelect: FC<{
  disabled?: boolean
  contractId?: number | string
}> = ({ contractId, disabled }) => {
  const { t } = useTranslation()
  const history = useHistory()

  const { data: contractsData, loading } = useGetUserContractsQuery()
  const { resetHistory } = useCustomHistory()

  const [getActionSignatureRequestsCount] = useActionSignatureRequestsCountLazyQuery()
  const [owner, setOwner] = useState<string>('')
  const [expanded, setExpanded] = useState<string | false>(false)
  const [showRejected, setShowRejected] = useState<boolean>(false)
  const { resetAll } = usePageFiltersSorting()
  const { docsCount, refetchDocsCount } = useTotalUnreadDocCount()

  const user = useContext(CurrentUserContext)
  const { userRights } = useDetermineUserRights(contractId as string | number)
  const { contractsAp, contractsApplicant, individualContract } = useMemo(
    () =>
      getDetailsContractsByIndividual(
        contractsData?.contracts as BankableEntityContractType[],
        user as Individual,
      ),
    [contractsData, user],
  )

  const { currentContract, contractsList } = useMemo(() => {
    const fullContractsList = [
      ...contractsAp,
      ...contractsApplicant,
      ...(individualContract ? [individualContract] : []),
    ]

    return fullContractsList.reduce(
      (
        acc: {
          currentContract: undefined | BankableEntityContractType
          contractsList: BankableEntityContractType[]
        },
        contract,
      ) => {
        if (Number(contract.id) === Number(contractId)) {
          acc.currentContract = contract
        } else if (contract.id !== individualContract?.id) {
          acc.contractsList.push(contract)
        }
        return acc
      },
      { currentContract: undefined, contractsList: [] },
    )
  }, [individualContract, contractsAp, contractsApplicant, contractId])

  const sortedContractsList = useMemo(() => {
    const sortedContracts = orderBy(
      contractsList,
      [
        (item) => {
          const isActive = isActiveContract(item, user as Individual)
          const displayKey = isActive
            ? 'activeContract'
            : displayStatusKeys[item.status as string] || ''
          const index = CONTRACT_STATUS_ORDER.indexOf(
            displayKey as typeof CONTRACT_STATUS_ORDER[number],
          )

          return index === -1 ? CONTRACT_STATUS_ORDER.length : index
        },
        (item) => (item.owner as LegalEntity)?.companyName?.toLowerCase() || '',
      ],
      ['asc', 'asc'],
    )

    const isNotActiveIndividualContract =
      individualContract && individualContract.id !== Number(contractId)

    return [...(isNotActiveIndividualContract ? [individualContract] : []), ...sortedContracts]
  }, [contractsList, individualContract, contractId])

  const indexOfFirstRejectedContract = sortedContractsList.findIndex(
    (contract) => contract.status === ContractStatusType.Rejected,
  )
  const hasRejected = indexOfFirstRejectedContract !== -1

  const { isLimitedPerson, isStrictLimitedPerson } = useLimitedPerson(currentContract)

  const allowContractDataView =
    (user?.isPhoneVerified &&
      ([
        ContractStatusType.Suspended,
        ContractStatusType.Terminated,
        ContractStatusType.Monitoring,
        ContractStatusType.TerminationPending,
      ].includes(currentContract?.status as ContractStatusType) ||
        (currentContract?.status === ContractStatusType.Active &&
          [individualContract, ...contractsAp].some(
            (contract) => Number(contract?.id) === Number(contractId),
          ))) &&
      !isStrictLimitedPerson) ||
    userRights?.isIntroducerContractOwner

  const { resetSearchAndPageValues } = usePageFiltersSorting()

  const isPersonalContract = userRights?.isIndividualContractOwner

  const isOnboarding = useMemo(() => history.location.pathname.includes('onboarding'), [
    history.location.pathname,
  ])

  const isUserContractAP = useMemo(
    () => !!currentContract?.authorizedPersons?.find((ap) => ap?.person?.id === user?.id),
    [user, currentContract],
  )

  const isHiddenDots = useMemo(() => {
    if (!isUserContractAP) return true
    return !user?.isVerified || isLimitedPerson || !docsCount
  }, [isUserContractAP, isLimitedPerson, docsCount, user])

  const onView = useCallback(
    (contract: BankableEntityContractType) => {
      history.push(
        generatePath(APP_PATHS.application.status, {
          [PATH_PARAMS.applicationId]: contract.id,
        }),
      )
    },
    [history],
  )
  const toggleRejected = () => {
    setShowRejected((prevState) => !prevState)
  }

  const handleExpand = useCallback(
    (panel: string) => (event: React.ChangeEvent<unknown>) => {
      event.stopPropagation()

      if (!!expanded) {
        setExpanded(false)
        return
      }
      setExpanded(panel)
    },
    [setExpanded, expanded],
  )

  const onEdit = useCallback(
    (contract: BankableEntityContractType) => {
      let redirectPath = APP_PATHS.application.review
      if (contract.status === ContractStatusType.Draft) {
        if (
          (contract.owner as LegalEntity).legalEntityType === UboLegalEntityType.Unknown &&
          contract.type !== ContractType.IntroducerReference
        ) {
          redirectPath = APP_PATHS.application.edit
        } else if (
          isEmpty(contract.authorizedPersons) &&
          contract.type !== ContractType.IntroducerReference
        ) {
          redirectPath = APP_PATHS.application.authorizedPersons.add
        } else if (isEmpty((contract.owner as LegalEntity).legalEntityDirectors)) {
          redirectPath = APP_PATHS.application.directors.list
        } else if (isEmpty(contract.ubos)) {
          redirectPath = APP_PATHS.application.beneficialOwners.list
        } else if (isEmpty(contract.contacts)) {
          redirectPath = APP_PATHS.application.contacts.list
        }
      } else if (contract.status === ContractStatusType.PreAssessmentDraft) {
        redirectPath = APP_PATHS.application.edit
      }
      history.push(generatePath(redirectPath, { [PATH_PARAMS.applicationId]: contract.id }))
    },
    [history],
  )

  const actions: { [key in AppActions]: (contract: BankableEntityContractType) => void } = {
    [AppActions.View]: onView,
    [AppActions.Edit]: onEdit,
    [AppActions.Delete]: () => true,
  }

  const handleChange = useCallback(
    async (contract: BankableEntityContractType) => {
      setExpanded(false)
      goBackPathVar('')
      resetHistory()
      isUnsaveFormData(false)
      const specialContractStatuses = [
        ContractStatusType.FirstLinePending,
        ContractStatusType.AmloPending,
        ContractStatusType.SignaturesPending,
      ]

      if (
        (contract.owner as LegalEntity).legalEntityType === UboLegalEntityType.Unknown &&
        contract.type !== ContractType.IntroducerReference
      ) {
        history.push(
          generatePath(APP_PATHS.application.edit, { [PATH_PARAMS.applicationId]: contract?.id }),
        )
      } else {
        const { data } = await getActionSignatureRequestsCount({
          variables: { contractId: contract.id, statuses: [ActionSignatureStatus.Pending] },
          fetchPolicy: 'network-only',
        })

        const pathToRedirect = !!data?.actionSignatureRequestsCount
          ? APP_PATHS.dashboard.actions
          : contract.type === ContractType.IntroducerReference
          ? APP_PATHS.dashboard.partnerManagementOverview
          : APP_PATHS.dashboard.home

        if (
          (isActiveContract(contract, user as Individual) ||
            isLimitedActiveContract(contract, user as Individual)) &&
          contract.status &&
          !specialContractStatuses.includes(contract.status)
        ) {
          resetSearchAndPageValues()
          history.push(generatePath(pathToRedirect, { [PATH_PARAMS.applicationId]: contract?.id }))
        } else {
          actions[statusActions[contract.status as ContractStatusType][0]](
            contract as BankableEntityContractType,
          )
        }
      }
      localStorage.clear()
      resetAll()
    },
    [history, resetSearchAndPageValues, getActionSignatureRequestsCount],
  )

  const getContractTabUrl = (tab: CmTabName) => {
    return generatePath(APP_PATHS.dashboard.contractManagement.root, {
      [PATH_PARAMS.applicationId]: contractId,
      [PATH_PARAMS.contractManagementTab]: tab,
    })
  }

  const LocationCheck =
    history.location.pathname ===
    getContractTabUrl(isPersonalContract ? CmTabName.PersonalProfile : CmTabName.BusinesDetails)

  const navigateContractManagementTab = (tab: CmTabName) => {
    history.push(getContractTabUrl(tab))
  }

  const addNewApp = useCallback(() => {
    history.push(APP_PATHS.root, { from: 'dashboard' })
  }, [history])

  useEffect(() => {
    if (!isEmpty(contractsData?.contracts)) {
      const nameOwner = (contractsData?.contracts as BankableEntityContractType[]).find(
        (contract) => {
          // eslint-disable-next-line eqeqeq
          return contractId == contract.id
        },
      )
      setOwner(
        nameOwner?.ownerType === UboType.NaturalPerson
          ? t('personal')
          : (nameOwner?.owner as LegalEntity)?.companyName ?? '',
      )
    }
    return () => {
      setOwner('')
    }
  }, [contractId, contractsData])

  useEffect(() => {
    refetchDocsCount().then()
  }, [contractId])

  const handleClose = () => {
    if (!history.location.pathname.includes('contract-management')) {
      setExpanded(false)
    }
    return
  }

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <StyledAccordion
        square
        expanded={!!expanded}
        onChange={handleExpand('contract-select')}
        data-test="autotest-contractSelect"
      >
        <StyledAccordionSummary
          expandIcon={!loading ? <ExpandMoreIcon /> : null}
          aria-controls="contract-select"
          id="contract-select"
          disabled={disabled}
          data-test="contractSelector"
        >
          {owner && (
            <>
              <BusinessIcon />
              <StyledTypography component="div" data-test="owner">
                <ContractAlertDot size={12} hidden={isHiddenDots} />
                <Box component={'p'} sx={{ margin: 0, wordBreak: 'break-word', fontWeight: 600 }}>
                  {owner}
                </Box>
              </StyledTypography>
            </>
          )}
        </StyledAccordionSummary>

        <StyledAccordionDetails>
          <List id="menu-list-grow">
            {!isEmpty(sortedContractsList) &&
              sortedContractsList.map((contract, index) => (
                <React.Fragment key={contract.id}>
                  {indexOfFirstRejectedContract === index && (
                    <StyledFormLabel
                      value="start"
                      onClick={(e) => e.stopPropagation()}
                      control={
                        <Switch
                          checked={showRejected}
                          size="small"
                          color="primary"
                          onChange={toggleRejected}
                        />
                      }
                      label={
                        showRejected
                          ? t('hideRejected', 'Hide rejected')
                          : t('showRejected', 'Show rejected')
                      }
                      labelPlacement="start"
                    />
                  )}
                  {hasRejected && index >= indexOfFirstRejectedContract && !showRejected ? null : (
                    <ContractsListItem
                      contract={contract}
                      isSelected={Number(contractId) === contract.id}
                      handleChangeContract={handleChange}
                      rejected={index >= indexOfFirstRejectedContract}
                    />
                  )}
                </React.Fragment>
              ))}

            {!(user?.isIntroducer && (contractsData?.contracts?.length ?? 0) > 0) && (
              <StyledListItem
                onClick={addNewApp}
                sx={{
                  textDecoration: 'underline',
                }}
                data-test="autotest-addNewApplication"
              >
                <ListItemText data-test="addNewApp">
                  {t('addNewApplication', 'Add new application')}
                </ListItemText>
              </StyledListItem>
            )}
          </List>

          {!isOnboarding && (
            <Box>
              <Divider
                sx={{ height: 1, backgroundColor: '#E5E7EB', marginY: 1.25, marginX: 0 }}
                orientation="horizontal"
                flexItem
              />

              {!userRights?.isIntroducerContractOwner && allowContractDataView && (
                <List>
                  <StyledListItem
                    active={LocationCheck}
                    onClick={() =>
                      navigateContractManagementTab(
                        isPersonalContract ? CmTabName.PersonalProfile : CmTabName.BusinesDetails,
                      )
                    }
                  >
                    <Typography
                      sx={{
                        position: 'relative',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        lineHeight: '2rem',
                        paddingRight: 1.65,
                      }}
                    >
                      {isPersonalContract
                        ? t('personalProfile', 'Personal profile')
                        : t('businessDetails', 'Business details')}
                    </Typography>
                  </StyledListItem>
                </List>
              )}

              <SubAccordionMenu
                contractsApplicant={contractsApplicant}
                currentContract={currentContract}
              />
            </Box>
          )}
        </StyledAccordionDetails>
      </StyledAccordion>
    </ClickAwayListener>
  )
}
